import { useEffect, useState } from 'react';
import {
  createRole,
  deleteRole,
  getAccess,
  setAccess,
  setRoleFlags,
  type Access,
  type Role,
} from './admin-api';
import { useUser } from './user-context';

// Матрица «роль × экран» + флаги роли (скрыть финансы / админ) + создание/удаление ролей.
export function RolesPanel() {
  const me = useUser();
  const [data, setData] = useState<Access | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ key: '', name: '', ico: '', descr: '' });

  const load = () => getAccess().then(setData);
  useEffect(() => {
    load();
  }, []);

  async function toggle(role: string, screen: string, on: boolean) {
    const cell = `${role}:${screen}`;
    setBusy(cell);
    setMsg(null);
    setData((d) => {
      if (!d) return d;
      const cur = new Set(d.matrix[role] ?? []);
      if (on) cur.add(screen);
      else cur.delete(screen);
      return { ...d, matrix: { ...d.matrix, [role]: [...cur] } };
    });
    try {
      await setAccess(role, screen, on);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Не удалось изменить доступ.');
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function flag(role: Role, field: 'hides_finance' | 'is_admin', on: boolean) {
    const cell = `${role.key}:${field}`;
    setBusy(cell);
    setMsg(null);
    setData((d) =>
      d
        ? {
            ...d,
            roles: d.roles.map((r) => (r.key === role.key ? { ...r, [field]: on ? 1 : 0 } : r)),
          }
        : d,
    );
    try {
      await setRoleFlags(role.key, { [field]: on });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Не удалось изменить флаг роли.');
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function remove(role: Role) {
    if (!window.confirm(`Удалить роль «${role.name}»? Действие необратимо.`)) return;
    setBusy(`del:${role.key}`);
    setMsg(null);
    try {
      await deleteRole(role.key);
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Не удалось удалить роль.');
    } finally {
      setBusy(null);
    }
  }

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    setBusy('new');
    setMsg(null);
    try {
      await createRole({
        key: form.key.trim().toLowerCase(),
        name: form.name.trim(),
        ico: form.ico.trim() || undefined,
        descr: form.descr.trim() || undefined,
      });
      setForm({ key: '', name: '', ico: '', descr: '' });
      setAdding(false);
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Не удалось создать роль.');
    } finally {
      setBusy(null);
    }
  }

  if (!data) return <p className="muted">Загрузка…</p>;

  const navScreens = data.screens.filter((s) => !s.key.startsWith('cfg_'));
  const cfgScreens = data.screens.filter((s) => s.key.startsWith('cfg_'));

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
        <p className="muted small grow" style={{ margin: 0 }}>
          Экраны — что видит роль в меню. <b>Скрыть финансы</b> — прятать суммы/платежи/документы.{' '}
          <b>Админ</b> — доступ к бэкапу, аудиту, пользователям, ролям и пресетам. Изменения
          применяются при следующем обновлении страницы у пользователей роли.
        </p>
        <button className="btn sm" disabled={busy === 'new'} onClick={() => setAdding((v) => !v)}>
          {adding ? 'Отмена' : '+ Роль'}
        </button>
      </div>
      {msg && <p className="err">{msg}</p>}

      {adding && (
        <form className="glass pad" style={{ marginBottom: 14 }} onSubmit={submitNew}>
          <div className="fields">
            <label className="field">
              <span className="muted small">Ключ (латиница)</span>
              <input
                value={form.key}
                disabled={busy === 'new'}
                placeholder="logist"
                onChange={(e) => setForm({ ...form, key: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="muted small">Название</span>
              <input
                value={form.name}
                disabled={busy === 'new'}
                placeholder="Логист"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="muted small">Иконка</span>
              <input
                value={form.ico}
                disabled={busy === 'new'}
                placeholder="🚚"
                style={{ width: 70 }}
                onChange={(e) => setForm({ ...form, ico: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="muted small">Описание</span>
              <input
                value={form.descr}
                disabled={busy === 'new'}
                placeholder="Отгрузка и КПП"
                onChange={(e) => setForm({ ...form, descr: e.target.value })}
              />
            </label>
          </div>
          <button
            className="btn sm"
            type="submit"
            disabled={busy === 'new' || !form.key || !form.name}
          >
            Создать роль
          </button>
          <p className="muted small" style={{ marginTop: 8 }}>
            Экраны и флаги новой роли настраиваются в таблице ниже.
          </p>
        </form>
      )}

      <div className="tablewrap glass">
        <table className="tbl access-matrix">
          <thead>
            <tr>
              <th>Свойство \ Роль</th>
              {data.roles.map((r) => (
                <th key={r.key} className="c" title={r.descr ?? ''}>
                  <span className="am-ico">{r.ico}</span>
                  <span className="am-role">{r.name}</span>
                  <button
                    className="icon-btn"
                    style={{ fontSize: 11, padding: 0, marginTop: 2 }}
                    disabled={busy === `del:${r.key}` || r.key === me.role}
                    title={r.key === me.role ? 'Нельзя удалить свою роль' : 'Удалить роль'}
                    onClick={() => remove(r)}
                  >
                    ✕
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <b>🏭 Скрыть финансы</b>
              </td>
              {data.roles.map((r) => {
                const cell = `${r.key}:hides_finance`;
                return (
                  <td key={r.key} className="c">
                    <input
                      type="checkbox"
                      className="am-check"
                      checked={r.hides_finance === 1}
                      disabled={busy === cell}
                      onChange={(e) => flag(r, 'hides_finance', e.target.checked)}
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <td>
                <b>🛡 Админ-доступ</b>
              </td>
              {data.roles.map((r) => {
                const cell = `${r.key}:is_admin`;
                const selfLock = r.key === me.role && r.is_admin === 1;
                return (
                  <td key={r.key} className="c">
                    <input
                      type="checkbox"
                      className="am-check"
                      checked={r.is_admin === 1}
                      disabled={busy === cell || selfLock}
                      title={selfLock ? 'Нельзя снять админ-доступ со своей роли' : undefined}
                      onChange={(e) => flag(r, 'is_admin', e.target.checked)}
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <td
                colSpan={data.roles.length + 1}
                className="muted small"
                style={{ paddingTop: 10 }}
              >
                Экраны в меню:
              </td>
            </tr>
            {navScreens.map((s) => (
              <tr key={s.key} style={{ cursor: 'default' }}>
                <td>{s.name}</td>
                {data.roles.map((r) => {
                  const on = (data.matrix[r.key] ?? []).includes(s.key);
                  const cell = `${r.key}:${s.key}`;
                  return (
                    <td key={r.key} className="c">
                      <input
                        type="checkbox"
                        className="am-check"
                        checked={on}
                        disabled={busy === cell}
                        onChange={(e) => toggle(r.key, s.key, e.target.checked)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {cfgScreens.length > 0 && (
              <tr>
                <td
                  colSpan={data.roles.length + 1}
                  className="muted small"
                  style={{ paddingTop: 10 }}
                >
                  Разделы «Настроек» (у админ-роли — все автоматически):
                </td>
              </tr>
            )}
            {cfgScreens.map((s) => (
              <tr key={s.key} style={{ cursor: 'default' }}>
                <td>{s.name.replace('Настройки · ', '⚙ ')}</td>
                {data.roles.map((r) => {
                  const admin = r.is_admin === 1;
                  const on = admin || (data.matrix[r.key] ?? []).includes(s.key);
                  const cell = `${r.key}:${s.key}`;
                  // Свою вкладку «Права ролей» нельзя снять (иначе потеряешь эту панель).
                  const selfLock = s.key === 'cfg_roles' && r.key === me.role && !admin && on;
                  return (
                    <td key={r.key} className="c">
                      <input
                        type="checkbox"
                        className="am-check"
                        checked={on}
                        disabled={busy === cell || admin || selfLock}
                        title={
                          admin
                            ? 'Включено через админ-доступ'
                            : selfLock
                              ? 'Нельзя убрать «Права ролей» у своей роли'
                              : undefined
                        }
                        onChange={(e) => toggle(r.key, s.key, e.target.checked)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
